import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

type Tenant = {
  uid: string;
  name: string;
  abbreviation: string;
  description?: string;
  imageUrl?: string;
  active?: boolean;
};

import { resolveApiBaseUrl } from "../api/apiBase";

const API_BASE_URL = resolveApiBaseUrl();

const TENANTS_PATHS = ["/api/tenants", "/tenants"] as const;

const TenantsPage = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [active, setActive] = useState(true);

  const loadTenants = async (searchValue?: string) => {
    try {
      setLoading(true);
      setError(null);
      const q = (searchValue ?? "").trim();
      let lastErr: Error | null = null;
      for (const path of TENANTS_PATHS) {
        try {
          const url = new URL(`${API_BASE_URL}${path}`);
          if (q) {
            url.searchParams.set("search", q);
          }
          const response = await fetch(url.toString());
          if (!response.ok) {
            const data = await response.json().catch(() => null);
            if (response.status === 404) {
              lastErr = new Error(data?.message || "Endpoint not found");
              continue;
            }
            throw new Error(data?.message || `Failed to load tenants (HTTP ${response.status})`);
          }
          const data = await response.json().catch(() => null);
          if (!data?.success || !Array.isArray(data.tenants)) {
            setTenants([]);
            return;
          }
          setTenants(data.tenants as Tenant[]);
          return;
        } catch (err) {
          lastErr = err instanceof Error ? err : new Error(String(err));
          if ((err as Error).message?.includes("Endpoint not found") || (err as Error).message?.includes("404")) {
            continue;
          }
          throw err;
        }
      }
      throw lastErr ?? new Error("Failed to load tenants");
    } catch (err) {
      setError((err as Error).message || "Failed to load tenants");
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreateError(null);

    const trimmedName = name.trim();
    const trimmedAbbr = abbreviation.trim();

    if (!trimmedName || !trimmedAbbr) {
      setCreateError("Name and abbreviation are required.");
      return;
    }

    try {
      setCreating(true);
      let lastErr: Error | null = null;

      for (const path of TENANTS_PATHS) {
        try {
          const response = await fetch(`${API_BASE_URL}${path}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: trimmedName,
              abbreviation: trimmedAbbr,
              description: description.trim() || undefined,
              imageUrl: imageUrl.trim() || undefined,
              active,
            }),
          });

          const data = await response.json().catch(() => null);

          if (!response.ok) {
            const message =
              data?.message ||
              (response.status === 409
                ? "Abbreviation is already in use."
                : `Failed to create organization (HTTP ${response.status})`);
            if (response.status === 404) {
              lastErr = new Error(message);
              continue;
            }
            throw new Error(message);
          }

          if (!data?.success || !data.tenant) {
            throw new Error("Unexpected response from server when creating organization.");
          }

          // Clear form and refresh list
          setName("");
          setAbbreviation("");
          setDescription("");
          setImageUrl("");
          setActive(true);
          await loadTenants(search);
          return;
        } catch (err) {
          lastErr = err instanceof Error ? err : new Error(String(err));
          if ((err as Error).message?.includes("Endpoint not found") || (err as Error).message?.includes("404")) {
            continue;
          }
          throw err;
        }
      }

      throw lastErr ?? new Error("Failed to create organization.");
    } catch (err) {
      setCreateError((err as Error).message || "Failed to create organization.");
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    void loadTenants();
  }, []);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void loadTenants(search);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Organizations</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage organizations used for branded login and registration.
          </p>
        </div>
        <form onSubmit={handleSearchSubmit} className="flex gap-2 w-full sm:w-auto">
          <Input
            type="text"
            placeholder="Search by name or abbreviation"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full sm:w-64"
          />
          <Button type="submit" disabled={loading}>
            {loading ? "Searching..." : "Search"}
          </Button>
        </form>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configured organizations</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">Create new organization</h2>
            {createError && (
              <Alert variant="destructive" className="mb-3">
                <AlertDescription>{createError}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Name
                </label>
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Organization name"
                  disabled={creating}
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Abbreviation
                </label>
                <Input
                  value={abbreviation}
                  onChange={(event) => setAbbreviation(event.target.value)}
                  placeholder="Short code (e.g. org1)"
                  disabled={creating}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Description
                </label>
                <Input
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Optional description"
                  disabled={creating}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Logo URL
                </label>
                <Input
                  value={imageUrl}
                  onChange={(event) => setImageUrl(event.target.value)}
                  placeholder="https://example.com/logo.png"
                  disabled={creating}
                />
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <input
                  id="org-active"
                  type="checkbox"
                  checked={active}
                  onChange={(event) => setActive(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={creating}
                />
                <label htmlFor="org-active" className="text-sm text-gray-700 dark:text-gray-200">
                  Active
                </label>
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={creating}>
                  {creating ? "Creating..." : "Create organization"}
                </Button>
              </div>
            </form>
          </div>

          {!error && tenants.length === 0 && !loading && (
            <p className="text-gray-500 dark:text-gray-400">
              No organizations found yet. Use the form above to create the first one.
            </p>
          )}

          {loading && (
            <p className="text-gray-500 dark:text-gray-400">Loading tenants...</p>
          )}

          {!loading && tenants.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tenants.map((tenant) => (
                <div
                  key={tenant.uid}
                  className="border rounded-lg p-4 bg-white dark:bg-gray-900 flex flex-col gap-2"
                >
                  <div className="flex items-center gap-3">
                    {tenant.imageUrl && (
                      <img
                        src={tenant.imageUrl}
                        alt=""
                        className="h-8 w-8 rounded object-contain flex-shrink-0"
                      />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {tenant.name}
                        </span>
                        <Badge variant="outline">
                          {tenant.abbreviation}
                        </Badge>
                      </div>
                      {tenant.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {tenant.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span className="font-mono break-all">
                      {tenant.uid}
                    </span>
                    {tenant.active === false ? (
                      <Badge variant="destructive" className="text-[10px]">
                        Inactive
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">
                        Active
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantsPage;

